import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, CheckCircle } from 'lucide-react';
import useSiteImages from '../hooks/useSiteImages';
import { useSiteServices } from '../hooks/useSiteServices';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import './Services.css';

// Map service IDs to image keys
const SERVICE_IMAGE_KEYS = {
  'gartenpflege': 'service_gartenpflege',
  'baumfaellung': 'service_baumfaellung',
  'baggerarbeiten': 'service_baggerarbeiten',
  'entsorgung': 'service_entsorgung',
  'hochdruckreinigung': 'service_hochdruckreinigung',
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const { getServiceById, loading: servicesLoading } = useSiteServices();
  const { companyInfo } = useCompanyInfo();
  const service = getServiceById(serviceId);
  const { getImage } = useSiteImages();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  const getServiceImage = () => {
    const key = SERVICE_IMAGE_KEYS[serviceId];
    if (key) {
      return getImage(key);
    }
    return service?.image || '';
  };

  if (!service) {
    return (
      <div className="service-detail-page">
        <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>
          <h1 className="heading-1">Service nicht gefunden</h1>
          <Link to="/leistungen" className="btn-primary" style={{ marginTop: '24px' }}>
            Zurück zu Leistungen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      <section className="service-detail-hero">
        <div className="container">
          <div className="service-detail-hero-content">
            <h1 className="display-medium">{service.name}</h1>
            <p className="body-large" style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
              {service.description}
            </p>
          </div>
          <img src={getServiceImage()} alt={service.name} className="service-full-image" />
        </div>
      </section>

      <section className="service-content-section">
        <div className="container">
          <h2 className="heading-2" style={{ textAlign: 'center' }}>Unsere Leistungen im Detail</h2>
          
          <div className="service-features-grid">
            {service.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon">
                  <CheckCircle size={16} />
                </div>
                <span className="body-medium">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {service.faqs && service.faqs.length > 0 && (
        <section className="faq-section">
          <div className="container">
            <h2 className="heading-2" style={{ textAlign: 'center' }}>Häufig gestellte Fragen</h2>
            
            <div className="faq-list">
              {service.faqs.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer body-medium">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="service-cta-section">
        <div className="container">
          <div className="service-cta-card network-card">
            <h2 className="heading-2">Interesse an {service.name}?</h2>
            <p className="body-large" style={{ marginTop: '16px' }}>
              Kontaktieren Sie uns für eine individuelle Beratung und ein kostenloses Angebot
            </p>
            <div className="service-cta-buttons">
              <Link to="/kontakt" className="btn-cta">
                Angebot anfordern
              </Link>
              <a href={`tel:${companyInfo.contact.phone}`} className="btn-secondary">
                <Phone size={18} style={{ marginRight: '8px', display: 'inline-block', verticalAlign: 'middle' }} />
                Jetzt anrufen
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Local SEO Links */}
      <section className="service-local-links">
        <div className="container">
          <h2>{service.name} in Ihrer Nähe</h2>
          <div className="local-links-grid">
            {[
              ['goettingen', 'Göttingen'], ['northeim', 'Northeim'], ['einbeck', 'Einbeck'],
              ['duderstadt', 'Duderstadt'], ['noerten-hardenberg', 'Nörten-Hardenberg'], ['bovenden', 'Bovenden'],
              ['hardegsen', 'Hardegsen'], ['osterode', 'Osterode'], ['herzberg', 'Herzberg'],
              ['bad-lauterberg', 'Bad Lauterberg'], ['moringen', 'Moringen'], ['uslar', 'Uslar'],
              ['hann-muenden', 'Hann. Münden'], ['gieboldehausen', 'Gieboldehausen'], ['dransfeld', 'Dransfeld'],
            ].map(([slug, name]) => (
              <Link key={slug} to={`/${serviceId}/${slug}`} className="local-link-item">
                {service.name} {name}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
